<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('khmerSecondarySchedule.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('khmerSecondarySchedule.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <div class="row">
      <div class="col-lg-3 col-md-12 col-sm-12">
        <div class="mb-3">
          <ts-loading-banner :loading="loadingPreData">
            <ts-card>
              <ts-card-wrapper>
                <div class="form-group">
                  <label class="control-label required">{{
                    $t('khmerSecondarySchedule.academicYear')
                  }}</label>
                  <treeselect
                    :load-options="loadAcYearOptions"
                    :options="acYearOptions"
                    :placeholder="$t('typeForSearch')"
                    v-model="model.ac_year_id"
                  />
                  <div class="validate" v-if="errors.has('ac_year_id')">
                    {{ errors.first('ac_year_id') }}
                  </div>
                </div>
                <div class="form-group">
                  <label class="control-label required">{{
                    $t('khmerSecondarySchedule.programMain')
                  }}</label>
                  <treeselect
                    :load-options="loadProgramMain"
                    :options="acProgramMainOptions"
                    :placeholder="$t('typeForSearch')"
                    v-model="model.acp_id"
                  />
                  <div class="validate" v-if="errors.has('acp_id')">
                    {{ errors.first('acp_id') }}
                  </div>
                </div>
                <div class="form-group">
                  <div class="row">
                    <div class="col-lg-6">
                      <label class="control-label">{{
                        $t('khmerSecondarySchedule.dayFrom')
                      }}</label>
                      <select
                        v-model.number="model.wd_id_from"
                        class="form-control"
                        :class="{ 'is-invalid': errors.has('wd_id_from') }"
                      >
                        <option value>{{ $t('select') }}</option>
                        <option
                          v-for="(wd, index) in weekDays"
                          :key="index"
                          :value="wd.wd_id"
                          >{{ wd.wd_name_en + '|' + wd.wd_name_kh }}</option
                        >
                      </select>
                      <div
                        class="invalid-feedback"
                        v-if="errors.has('wd_id_from')"
                      >
                        {{ errors.first('wd_id_from') }}
                      </div>
                    </div>
                    <div class="col-lg-6">
                      <label class="control-label">{{
                        $t('khmerSecondarySchedule.dayTo')
                      }}</label>
                      <select
                        v-model.number="model.wd_id_to"
                        class="form-control"
                        :class="{ 'is-invalid': errors.has('wd_id_to') }"
                      >
                        <option value>{{ $t('select') }}</option>
                        <option
                          v-for="(wd, index) in weekDays"
                          :key="index"
                          :value="wd.wd_id"
                          >{{ wd.wd_name_en + '|' + wd.wd_name_kh }}</option
                        >
                      </select>
                      <div
                        class="invalid-feedback"
                        v-if="errors.has('wd_id_to')"
                      >
                        {{ errors.first('wd_id_to') }}
                      </div>
                    </div>
                  </div>
                </div>
              </ts-card-wrapper>
              <div class="section-divider"></div>
              <ts-card-wrapper>
                <div class="ts-flex-initial">
                  <div class="text-right">
                    <ts-button
                      @click="generate"
                      :disabled="generating"
                      :waiting="generating"
                      color="primary"
                      v-tooltip="$t('generate')"
                      class
                    >
                      <i class="fas fa-cogs"></i>
                    </ts-button>
                    <ts-button
                      @click="fetchData"
                      :disabled="loading"
                      :waiting="loading"
                      color="info"
                      class="mr-2 ml-2"
                      outline
                      v-tooltip="$t('view')"
                    >
                      <i class="fas fa-eye"></i>
                    </ts-button>
                    <ts-button
                      @click="generateToSchedule"
                      :disabled="scheduleGenerating"
                      :waiting="scheduleGenerating"
                      color="success"
                      outline
                      v-tooltip="
                        $t('khmerSecondarySchedule.generateToSchedule')
                      "
                      class="mr-2"
                    >
                      <i class="fas fa-calendar-check"></i>
                    </ts-button>
                    <b-dropdown
                      dropup
                      variant="outline-default"
                      right
                      v-tooltip="$t('export')"
                    >
                      <template v-slot:button-content>
                        <i
                          class="fas fa-circle-notch spin inline-block mr-1"
                          v-if="exporting"
                        ></i>
                        <span class="inline-block">
                          <i class="fas fa-file"></i>
                        </span>
                      </template>
                      <b-dropdown-item href="#" @click="exportExcel">
                        <i class="fas fa-file-excel ts-text-green-500"></i>
                        {{ $t('excel') }}
                      </b-dropdown-item>
                      <b-dropdown-item href="#" @click="exportPdf">
                        <i class="fas fa-file-pdf ts-text-red-500"></i>
                        {{ $t('pdf') }}
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>
                </div>
              </ts-card-wrapper>
            </ts-card>
          </ts-loading-banner>
        </div>
      </div>
      <div class="col-lg-9 col-md-12 col-sm-12">
        <panel title="Schedule List">
          <ts-loading-banner :loading="loading">
            <div id="schedule-list-content">
              <div class="schedule-list">
                <table class="table table-bordered ui-table ts-table table">
                  <colgroup>
                    <col />
                    <col class="morning" span="4" />
                    <col class="evening" span="4" />
                    <col class="morning" span="4" />
                    <col class="evening" span="4" />
                    <col class="morning" span="4" />
                    <col class="evening" span="4" />
                    <col class="morning" span="4" />
                    <col class="evening" span="4" />
                    <col class="morning" span="4" />
                    <col class="evening" span="4" />
                    <col class="morning" span="4" />
                    <col class="evening" span="4" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th rowspan="2" class="align-middle">
                        {{ $t('khmerSecondarySchedule.className') }}
                      </th>
                      <th colspan="8" class="text-center">
                        {{ $t('khmerSecondarySchedule.monday') }}
                      </th>
                      <th colspan="8" class="text-center">
                        {{ $t('khmerSecondarySchedule.tuesday') }}
                      </th>
                      <th colspan="8" class="text-center">
                        {{ $t('khmerSecondarySchedule.wednesday') }}
                      </th>
                      <th colspan="8" class="text-center">
                        {{ $t('khmerSecondarySchedule.thursday') }}
                      </th>
                      <th colspan="8" class="text-center">
                        {{ $t('khmerSecondarySchedule.friday') }}
                      </th>
                      <th colspan="8" class="text-center">
                        {{ $t('khmerSecondarySchedule.saturday') }}
                      </th>
                    </tr>
                    <tr>
                      <td
                        class="text-center ts-font-bold"
                        v-for="(item, key) in 8"
                      >
                        {{ $t(key + 1) }}
                      </td>
                      <td
                        class="text-center ts-font-bold"
                        v-for="(item, key) in 8"
                      >
                        {{ $t(key + 1) }}
                      </td>
                      <td
                        class="text-center ts-font-bold"
                        v-for="(item, key) in 8"
                      >
                        {{ $t(key + 1) }}
                      </td>
                      <td
                        class="text-center ts-font-bold"
                        v-for="(item, key) in 8"
                      >
                        {{ $t(key + 1) }}
                      </td>
                      <td
                        class="text-center ts-font-bold"
                        v-for="(item, key) in 8"
                      >
                        {{ $t(key + 1) }}
                      </td>
                      <td
                        class="text-center ts-font-bold"
                        v-for="(item, key) in 8"
                      >
                        {{ $t(key + 1) }}
                      </td>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(record, index) in schedules" :key="index">
                      <td class="text-center align-middle">
                        {{ record[0].class_name }}
                      </td>
                      <td
                        class="text-center align-middle cell"
                        v-for="(r, index) in record"
                        :key="index"
                      >
                        <a
                          href="#"
                          @click.prevent="showPopUp(r)"
                          v-tooltip="
                            $t('khmerSecondarySchedule.addSubjectCode')
                          "
                        >
                          <span
                            class="text-primary ts-font-bold"
                            v-if="r.sstc_id !== null"
                            >{{ r.subject_code }}</span
                          >
                          <span class="text-grey" v-else>
                            <i class="fas fa-calendar-plus"></i>
                          </span>
                        </a>
                        <div v-if="r.sstc_id !== null" class="overlay">
                          <div class="icon">
                            <a
                              href="#"
                              @click.prevent="showPopUp(r)"
                              v-tooltip="$t('edit')"
                              class="text-primary"
                            >
                              <i class="fas fa-edit fa-md mr-1"></i>
                            </a>
                            <a
                              href="#"
                              @click.prevent="destroy(r)"
                              v-tooltip="$t('delete')"
                              class="text-danger"
                            >
                              <i class="fas fa-trash fa-md"></i>
                            </a>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </ts-loading-banner>
        </panel>
        <ts-card v-if="subjectList">
          <ts-card-wrapper>
            <subject-table />
          </ts-card-wrapper>
        </ts-card>
        <a-modal
          v-model="visible"
          :title="title"
          :maskClosable="false"
          @ok="save"
          :confirmLoading="saving"
          :cancelText="$t('cancel')"
          :okText="$t('save')"
          :zIndex="1020"
          width="400px"
        >
          <div class="form-group">
            <label class="control-label required">{{
              $t('khmerSecondarySchedule.teacherSubjectCode')
            }}</label>
            <select
              v-model.number="sstc_id"
              class="form-control"
              :class="{ 'is-invalid': errors.has('sstc_id') }"
            >
              <option :value="null">{{ $t('select') }}</option>
              <optgroup
                v-for="(subject, key) in subjects"
                :key="key"
                :label="getOjectKey(subject)"
              >
                <option
                  :value="item.sstc_id"
                  v-for="(item, index) in getValueObject(subject)"
                  :key="index"
                  >{{ item.subject_code + ' => ' + item.teacher_name }}</option
                >
              </optgroup>
            </select>
            <div class="invalid-feedback" v-if="errors.has('sstc_id')">
              {{ errors.first('sstc_id') }}
            </div>
          </div>
        </a-modal>
      </div>
    </div>
  </div>
</template>
<script>
import { LOAD_ROOT_OPTIONS } from '@riophae/vue-treeselect'
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
import SubjectTable from './subject-table'
import { trim } from 'lodash'
import * as FileDownload from 'downloadjs'
import PreviewSchedule from './preview-schedule'
export default {
  name: 'khmer-secondary-schedule',
  components: {
    SubjectTable
  },
  data() {
    return {
      model: {
        acp_id: null,
        ac_year_id: null,
        wd_id_from: '',
        wd_id_to: ''
      },
      errors: new Errors(),
      loadingPreData: false,
      saving: false,
      loading: false,
      exporting: false,
      generating: false,
      scheduleGenerating: false,
      acYearOptions: null,
      acProgramMainOptions: null,
      visible: false,
      item: null,
      sstc_id: null
    }
  },
  created() {
    this.fetchResourceBeforeCreate()
  },
  computed: {
    ...mapState('khmerSecondarySchedule', [
      'weekDays',
      'schedules',
      'subjects'
    ]),
    subjectList() {
      return !_.isEmpty(this.subjects)
    },
    title() {
      if (this.item === null) return ''

      return (
        this.item.class_name +
        ' => ' +
        this.$t(this.item.wd_name_en) +
        ' (' +
        this.$t(this.item.st_time_en) +
        ')'
      )
    }
  },
  methods: {
    ...mapActions('khmerSecondarySchedule', [
      'fetchAcademicYear',
      'fetchProgramMain'
    ]),
    fetchResourceBeforeCreate() {
      this.loadingPreData = true
      this.$store
        .dispatch('khmerSecondarySchedule/dataBeforeCreate')
        .then(() => {
          this.loadingPreData = false
        })
        .catch(error => {
          this.loadingPreData = false
          this.$toasted.error(error.message)
        })
    },
    async loadAcYearOptions({ action /*, callback*/ }) {
      if (action === LOAD_ROOT_OPTIONS) {
        this.acYearOptions = null
        let response = await this.fetchAcademicYear()
        this.acYearOptions = response.data.map(el => ({
          id: el.ac_year_id,
          label: el.ac_year
        }))
      }
    },
    async loadProgramMain({ action /*, callback*/ }) {
      if (action === LOAD_ROOT_OPTIONS) {
        this.acProgramMainOptions = null
        let response = await this.fetchProgramMain()
        this.acProgramMainOptions = response.data.map(el => ({
          id: el.acp_id,
          label: el.acp_name_foreign
        }))
      }
    },

    async generate() {
      this.errors = new Errors()
      this.generating = true

      this.$store
        .dispatch('khmerSecondarySchedule/generate', this.model)
        .then(response => {
          this.generating = false
          this.$toasted.success(response.message)
          this.fetchData()
          this.clear()
        })
        .catch(error => {
          this.generating = false
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
    },
    async fetchData() {
      this.errors = new Errors()
      this.loading = true
      await this.$store
        .dispatch('khmerSecondarySchedule/fetch', this.model)
        .then(response => {
          if (response.schedule.length <= 0) {
            this.$toasted.info(
              'Action failed! Press button Generate to create schedule template.'
            )
          }
          this.loading = false
        })
        .catch(error => {
          this.$store.commit('khmerSecondarySchedule/SET_SCHEDULE', [])
          this.$store.commit('khmerSecondarySchedule/SET_SUBJECT', [])
          this.loading = false
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
    },
    showPopUp(data) {
      this.item = data
      this.sstc_id = data.sstc_id
      this.visible = true
    },
    async destroy(record) {
      if (await this.$confirmModal.danger(this.$t('are_you_sure_to_delete'))) {
        record._deleting = true
        this.$store
          .dispatch('khmerSecondarySchedule/destroy', record.stp_id)
          .then(response => {
            this.fetchData()
            this.$toasted.success(response.message)
            record._deleting = false
          })
          .catch(error => {
            this.$toasted.error(error.message)
            record._deleting = false
          })
      }
    },
    async generateToSchedule() {
      this.errors = new Errors()
      this.scheduleGenerating = true

      this.$store
        .dispatch('khmerSecondarySchedule/generateToSchedule', this.model)
        .then(response => {
          this.scheduleGenerating = false
          this.$toasted.success(response.message)
          this.fetchData()
          this.clear()
        })
        .catch(error => {
          this.scheduleGenerating = false
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
    },
    clear() {
      this.model.wd_id_from = ''
      this.model.wd_id_to = ''
    },
    exportExcel() {
      this.errors = new Errors()
      this.exporting = true

      this.$store
        .dispatch('khmerSecondarySchedule/exportExcel', this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
        .finally(() => (this.exporting = false))
    },
    exportPdf() {
      this.$modal.show(
        PreviewSchedule,
        {
          data: this.model
        },
        {
          height: 'auto',
          width: '70%',
          scrollable: true,
          draggable: '.form-header',
          clickToClose: false
        }
      )
    },
    ...mapActions('khmerSecondarySchedule', ['updateSchedule']),
    getOjectKey(value) {
      return Object.keys(value).reduce((init, value) => (init = value), null)
    },
    getValueObject(obj) {
      return Object.values(obj).flat()
    },
    save() {
      this.errors = new Errors()
      this.saving = true

      this.updateSchedule({
        stp_id: this.item.stp_id,
        data: {
          sstc_id: this.sstc_id
        }
      })
        .then(response => {
          this.saving = false
          this.$toasted.success(response.message)
          this.fetchData()
          this.visible = false
        })
        .catch(error => {
          this.saving = false
          this.errors = new Errors(error.errors)

          this.$toasted.error(error.message)
        })
    }
  },
  beforeRouteLeave(to, from, next) {
    if (_.isEmpty(to.params)) {
      this.$store.commit('khmerSecondarySchedule/SET_SCHEDULE', [])
      this.$store.commit('khmerSecondarySchedule/SET_SUBJECT', [])
      this.$store.commit('khmerSecondarySchedule/RECEIVE_ACADEMIC_YEARS', [])
      this.$store.commit(
        'khmerSecondarySchedule/RECEIVE_ACADEMIC_PROGRAM_MAINS',
        []
      )
    }
    next()
  }
}
</script>
<style lang="scss" scoped>
.section-divider {
  border-bottom: 1px solid #eee;
}
#schedule-list-content {
  min-height: 27vh;
  max-height: auto;
  overflow-y: scroll;
  overflow-x: scroll;
}

.schedule-list {
  max-height: 39vh;
  width: 100px;

  table th,
  td {
    padding: 8px 12px;
    vertical-align: middle !important;
    white-space: nowrap;
  }
}
::-webkit-scrollbar {
  height: 8px;
  width: 3px; /* Remove scrollbar space */
  background: transparent; /* Optional: just make scrollbar invisible */
}

/* Optional: show position indicator in red */
::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background: #dddedf;
}
div::-webkit-scrollbar-thumb:hover {
  background: #818485;
}
.overlay {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100%;
  width: 100%;
  opacity: 0;
  transition: opacity 0.5s ease;
  -webkit-transition: opacity 0.5s ease;
  -moz-transition: opacity 0.5s ease;
  -o-transition: opacity 0.5s ease;
  background-color: #e2e8f0;
  z-index: 1000;
}

.table {
  margin-left: auto;
  margin-right: auto;
}

.table .cell {
  position: relative;
  max-width: 150px;
  max-height: 150px;
  text-align: center;
}
.cell:hover .overlay {
  opacity: 1;
}

.icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  -webkit-transform: translate(-50%, -50%);
  -moz-transform: translate(-50%, -50%);
  -ms-transform: translate(-50%, -50%);
  text-align: center;
}
.morning {
  background-color: #f0f3f4;
}
.evening {
  background-color: #fff;
}
</style>
